import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  CheckCircle2,
  AlertTriangle,
  Image as ImageIcon,
  UserRound,
  Eye,
  Signature,
} from "lucide-react";

import { getSafetyChecklist } from "../../../api";
import { resolveMediaUrl } from "../../../lib/utils";
import SafetyChecklistSectionHeader from "../shared/SafetyChecklistSectionHeader";
import { buildSectionedQuestionRows } from "../shared/safetySectionUtils";
const getInstructionLines = (text) => {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/\t+/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim(),
    )
    .filter(Boolean);
};

const InstructionBlock = ({ image, text, className = "" }) => {
  const imageUrl = resolveMediaUrl(image);
  const lines = getInstructionLines(text);

  if (!imageUrl && !lines.length) {
    return null;
  }

  return (
    <div
      className={`rounded-2xl border border-border bg-card p-4 shadow-sm ${className}`}
    >
      <div className="mb-3">
        <h3 className="text-sm font-bold text-foreground">Instructions</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Please read the instruction before checking the points.
        </p>
      </div>

      {imageUrl && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/20 p-3 mb-3">
          <img
            src={imageUrl}
            alt="Instruction"
            className="max-h-[300px] w-full object-contain rounded-lg"
          />
        </div>
      )}

      {lines.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {lines.map((line, index) => (
                <tr key={`${line}-${index}`}>
                  {index === 0 && (
                    <td
                      rowSpan={lines.length}
                      className="w-32 border border-border bg-muted/30 px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-muted-foreground align-middle"
                    >
                      Instructions
                    </td>
                  )}

                  <td className="border border-border bg-white px-3 py-2 font-semibold text-foreground">
                    {line}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} · ${timePart}`;
};

const getOptionToneByIndex = (index) => {
  if (index === 0) {
    return {
      active: "border-green-500 bg-green-500 text-white shadow-sm",
      idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
    };
  }

  if (index === 1) {
    return {
      active: "border-red-500 bg-red-500 text-white shadow-sm",
      idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    };
  }

  if (index === 2) {
    return {
      active: "border-yellow-500 bg-yellow-500 text-white shadow-sm",
      idle: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
    };
  }

  return {
    active: "border-slate-500 bg-slate-500 text-white shadow-sm",
    idle: "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
  };
};

const getAnswerBadgeClass = (answer) => {
  const value = String(answer || "").toUpperCase();

  if (value === "YES") {
    return "border-green-500 bg-green-100 text-green-700";
  }

  if (value === "NO") {
    return "border-red-500 bg-red-100 text-red-700";
  }

  if (value === "NA") {
    return "border-yellow-500 bg-yellow-100 text-yellow-700";
  }

  return "border-border bg-muted text-foreground";
};

const getQuestionStatus = (sub, pendingStatusLabel) => {
  const status = String(sub?.status || "").toLowerCase();

  if (status.includes("reject") || sub?.checker_action === "REJECTED") {
    return "rejected";
  }

  if (status === "completed" || status === "approved") {
    return "approved";
  }

  return pendingStatusLabel === "Pending for Rectification"
    ? "pending_rectification"
    : "pending_review";
};

const getChecklistStatusBadgeClasses = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (
    normalized === "in review" ||
    normalized === "pending for review"
  ) {
    return "border border-yellow-200 bg-yellow-100 text-orange-700";
  }

  if (normalized === "pending for rectification") {
    return "border border-purple-200 bg-purple-100 text-purple-700";
  }

  if (normalized === "completed" || normalized === "approved") {
    return "border border-green-200 bg-green-100 text-green-700";
  }

  if (normalized === "rejected" || normalized === "closed_rejected") {
    return "border border-red-200 bg-red-100 text-red-700";
  }

  return "border border-slate-200 bg-slate-100 text-slate-700";
};

const ChecklistStatusBadge = ({ status }) => {
  if (!status) return null;

  let displayText = status;
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "pending for review") {
    displayText = "In Review";
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getChecklistStatusBadgeClasses(
        displayText
      )}`}
    >
      {displayText}
    </span>
  );
};

const LEGACY_READONLY_HEADER_FIELDS = [
  { key: "format_no", label: "Format No.", order_index: 1 },
  { key: "revision_no", label: "Revision No.", order_index: 2 },
  { key: "issued_date", label: "Issued Date", order_index: 3 },
  { key: "revision_date", label: "Revision Date", order_index: 4 },
  { key: "project", label: "Project", order_index: 5 },
  {
    key: "inspection_report_no",
    label: "Inspection Report No.",
    order_index: 6,
  },
  { key: "name_of_contractor", label: "Name of Contractor", order_index: 7 },
  { key: "date_of_inspection", label: "Date of Inspection", order_index: 8 },
  { key: "make_model", label: "Make / Model", order_index: 9 },
  { key: "identification_no", label: "Identification No.", order_index: 10 },
  { key: "location", label: "Location", order_index: 11 },
  { key: "name_of_operator", label: "Name of Operator", order_index: 12 },
];

const sortReadonlyHeaderFields = (fields = []) =>
  [...fields].sort(
    (a, b) =>
      Number(a.row_index || 0) - Number(b.row_index || 0) ||
      Number(a.column_index || 0) - Number(b.column_index || 0) ||
      Number(a.order_index || 0) - Number(b.order_index || 0),
  );

const getReadonlyHeaderFields = (detail) => {
  /*
        New dynamic checklist response.
        Backend already resolves:
        - fixed template values
        - Maker-entered values
        - generated report number
        - inspection date
        - project/context values
    */
  const dynamicFields = Array.isArray(detail?.report_header_fields)
    ? detail.report_header_fields
    : [];

  if (dynamicFields.length > 0) {
    return sortReadonlyHeaderFields(
      dynamicFields.filter((field) => field.visible_in_preview !== false),
    );
  }

  /*
        Backward compatibility for old checklists that were created
        before dynamic header fields were implemented.
    */
  const legacyMeta =
    detail?.report_header_meta ||
    detail?.safety_report_meta ||
    detail?.report_meta ||
    {};

  return LEGACY_READONLY_HEADER_FIELDS.map((field) => ({
    ...field,
    value: legacyMeta[field.key] || "",
    visible_in_preview: true,
  }));
};

const ReadonlyInfoValue = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-muted/30 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </p>

    <p className="mt-1 break-words text-sm font-semibold text-foreground">
      {String(value ?? "").trim() || "—"}
    </p>
  </div>
);

const ReadonlyInfoCard = ({ detail }) => {
  const fields = getReadonlyHeaderFields(detail);

  if (!fields.length) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-foreground">
          Checklist Information
        </h3>

        {/* <p className="mt-1 text-xs text-muted-foreground">
          Read-only checklist information
        </p> */}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map((field) => (
          <ReadonlyInfoValue
            key={field.key}
            label={field.label}
            value={field.value ?? field.default_value ?? ""}
          />
        ))}
      </div>
    </div>
  );
};

const getLatestCheckerIdFromSubmissions = (detail) => {
  for (const item of detail?.items || []) {
    for (const sub of item?.submissions || []) {
      if (sub?.checker_id) {
        return `User ${sub.checker_id}`;
      }
    }
  }

  return "";
};

const getLatestNameFromSubmissions = (detail, keys = []) => {
  for (const item of detail?.items || []) {
    for (const sub of item?.submissions || []) {
      for (const key of keys) {
        const value = String(sub?.[key] || "").trim();

        if (value) {
          return value;
        }
      }
    }
  }

  return "";
};

const getLatestSignatureFromDetail = (detail, role) => {
  if (!detail) return "";

  if (role === "maker") {
    return (
      detail.latest_maker_signature ||
      detail.maker_signature ||
      detail.maker_signature_url ||
      detail.maker_signature_file ||
      detail.created_by_signature ||
      ""
    );
  }

  if (role === "checker") {
    return (
      detail.latest_checker_signature ||
      detail.checker_signature ||
      detail.checker_signature_url ||
      detail.checker_signature_file ||
      detail.verified_by_signature ||
      ""
    );
  }

  return "";
};

const formatSignedDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  return formatter
    .format(date)
    .replace(",", "")
    .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
};

const getSignOffUsers = (detail) => {
  const makerName =
    detail?.maker_name ||
    detail?.created_by_name ||
    detail?.created_by ||
    getLatestNameFromSubmissions(detail, [
      "latest_maker_name",
      "maker_name",
      "created_by_name",
    ]) ||
    "Maker";

  const contractorSignOff = detail?.contractor_checker_signoff;
  const contractorChecker = contractorSignOff
    ? {
        role: "contractor_checker",
        label: "Contractor Verified by",
        name:
          contractorSignOff.actor_name ||
          detail?.contractor_checker_name ||
          "Contractor Checker",
        designation: "Contractor Safety Officer",
        signature: contractorSignOff.signature || "",
        signedAt: contractorSignOff.signed_at || "",
        action: contractorSignOff.action || "",
      }
    : null;

  let checkerName =
    detail?.checker_name ||
    detail?.verified_by_name ||
    detail?.approved_by_name ||
    getLatestNameFromSubmissions(detail, [
      "latest_checker_name",
      "checker_name",
      "approved_by_name",
    ]) ||
    getLatestCheckerIdFromSubmissions(detail) ||
    "Checker";

  const makerSignature =
    detail?.maker_signature ||
    detail?.maker_signature_url ||
    getLatestSignatureFromDetail(detail, "maker") ||
    "";

  let checkerSignature =
    detail?.checker_signature ||
    detail?.checker_signature_url ||
    getLatestSignatureFromDetail(detail, "checker") ||
    "";

  const makerSignedAt =
    detail?.maker_signed_at ||
    detail?.created_at ||
    "";

  let checkerSignedAt =
    detail?.checker_signed_at ||
    "";

  // Prevent contractor checker's earlier rejection/approval from leaking into the final internal checker card
  if (contractorChecker) {
    const isContractorDuplicate =
      (hasValidSignature(contractorChecker.signature) &&
        checkerSignature === contractorChecker.signature) ||
      (contractorChecker.name && checkerName === contractorChecker.name);

    if (isContractorDuplicate && detail?.status !== "completed") {
      checkerSignature = "";
      checkerSignedAt = "";
    }
  }

  return {
    maker: {
      role: "maker",
      label: "Checked by",
      name: makerName,
      designation: "Maker",
      signature: makerSignature,
      signedAt: makerSignedAt,
    },
    contractorChecker,
    checker: {
      role: "checker",
      label: "Verified by",
      name: checkerName,
      designation: "Checker",
      signature: checkerSignature,
      signedAt: checkerSignedAt,
    },
  };
};

const SignatureViewModal = ({ open, onClose, user }) => {
  if (!open || !user) return null;

  const signatureUrl = resolveMediaUrl(user.signature);

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-border bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-foreground">
              {user.label}
            </h3>

            <p className="mt-1 text-sm text-muted-foreground">
              {user.name}{" "}
              {user.designation ? (
                <span className="font-medium">({user.designation})</span>
              ) : null}
            </p>

            {user.signedAt ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Signed on:{" "}
                <span className="font-medium text-foreground">
                  {formatSignedDateTime(user.signedAt)}
                </span>
              </p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          {signatureUrl ? (
            <div className="rounded-xl border border-border bg-muted/20 p-4">
              <img
                src={signatureUrl}
                alt={`${user.designation || user.label} signature`}
                className="mx-auto max-h-72 w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 text-center">
              <Signature className="mb-2 h-8 w-8 text-muted-foreground" />

              <p className="text-sm font-semibold text-foreground">
                No signature available
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                Latest signature was not found for this user.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const hasValidSignature = (signature) => {
  if (!signature) return false;
  const value = String(signature).trim();
  if (!value) return false;
  return !["null", "undefined", "none"].includes(value.toLowerCase());
};

const SignOffRow = ({ user, onView }) => {
  const hasSignature = hasValidSignature(user?.signature);

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <UserRound className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {user.label}
          </p>

          <p className="mt-0.5 break-words text-sm font-bold leading-snug text-foreground">
            {user.name}
          </p>

          {user.designation ? (
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {user.designation}
            </p>
          ) : null}

          {user.signedAt ? (
            <p className="mt-1 text-[10px] text-slate-400">
              Signed on: {formatSignedDateTime(user.signedAt)}
            </p>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onView(user)}
        disabled={!hasSignature}
        className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
          hasSignature
            ? "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            : "cursor-not-allowed border border-border bg-muted text-muted-foreground"
        }`}
      >
        <Eye className="h-3.5 w-3.5" />
        View
      </button>
    </div>
  );
};

const SignOffSection = ({ detail, onViewSignature }) => {
  const users = getSignOffUsers(detail);

  const signedUsers = [
    users.maker,
    users.contractorChecker,
    users.checker,
  ].filter((user) => user && hasValidSignature(user?.signature));

  if (signedUsers.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-foreground">Sign Off</h3>

        <p className="mt-1 text-xs text-muted-foreground">
          Available checklist signatures.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {signedUsers.map((user) => (
          <SignOffRow key={user.role} user={user} onView={onViewSignature} />
        ))}
      </div>
    </div>
  );
};

const ReadonlyQuestionCard = ({
  item,
  sub: passedSub,
  idx,
  displayIndex,
  pendingStatusLabel,
}) => {
  const sub = passedSub || item?.submissions?.[0];

  if (!sub) return null;

  const answer = sub?.latest_maker_answer || sub?.answer || "";
  const makerComment = sub?.latest_maker_remarks || sub?.maker_remarks || "";

  const checkerComment =
    sub?.latest_checker_remarks ||
    sub?.latest_checker_reject_remarks ||
    sub?.checker_remarks ||
    "";

  const makerPhoto =
    sub?.latest_maker_photo_url ||
    sub?.maker_photo_url ||
    sub?.photo_url ||
    sub?.image ||
    sub?.image_url ||
    sub?.media?.[0]?.file ||
    sub?.media?.[0]?.url ||
    "";

  const checkerPhoto =
    sub?.latest_checker_photo_url ||
    sub?.checker_photo_url ||
    sub?.checker_image ||
    sub?.review_image ||
    sub?.objection_image ||
    "";

  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm ${
        sub?.is_resubmitted
          ? "border-yellow-300 bg-yellow-50/60"
          : "border-border bg-card"
      }`}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
              {displayIndex ?? idx + 1}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold leading-relaxed text-foreground sm:text-base">
                  {item?.title}
                </h3>

                {sub?.is_resubmitted && (
                  <span className="inline-flex rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800">
                    Re-submitted by {sub?.latest_maker_name || "Maker"}
                  </span>
                )}
              </div>

              {item?.description && (
                <p className="mt-1 text-xs italic text-gray-500">
                  {item.description}
                </p>
              )}

              {item?.reference_image_url && (
                <div className="mt-3 mb-2">
                  <img
                    src={resolveMediaUrl(item.reference_image_url)}
                    alt="Reference"
                    className="max-h-24 cursor-pointer rounded border object-contain shadow-sm transition-opacity hover:opacity-90"
                    onClick={() =>
                      window.open(resolveMediaUrl(item.reference_image_url), "_blank")
                    }
                  />
                </div>
              )}

              {(item?.quantity_value || item?.quantity_unit) && (
                <div className="mt-2 mb-2 flex items-center gap-2 text-sm text-gray-700">
                  <span className="font-semibold">Quantity:</span> {item.quantity_value || "-"} {item.quantity_unit || ""}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 text-right text-xs text-muted-foreground sm:self-start">
          {formatDateTime(sub?.updated_at || sub?.created_at)}
        </div>
      </div>

      {/* ANSWER VALUE / OPTIONS */}
      {item?.input_type === "short_answer" ||
      item?.input_type === "text" ||
      item?.input_type === "paragraph" ||
      !Array.isArray(item?.options) ||
      item?.options?.length === 0 ? (
        <div className="mb-4 rounded-xl border border-border bg-muted/20 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Answer
          </p>
          <p className="text-sm font-bold text-foreground">
            {answer ? (
              answer
            ) : (
              <span className="font-normal italic text-muted-foreground">
                No answer provided
              </span>
            )}
          </p>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap gap-2">
          {item.options.map((opt, index) => {
            const selected =
              String(answer || "").toUpperCase() === String(opt).toUpperCase();
            const toneClasses = getOptionToneByIndex(index);

            return (
              <div
                key={opt}
                className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${
                  selected
                    ? toneClasses.active
                    : "border-border bg-muted/40 text-muted-foreground"
                }`}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}


      {/* MAKER COMMENT */}
      {makerComment && (
        <div className="mb-4 rounded-xl border border-border bg-muted/30 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Maker Comment
          </p>

          <p className="whitespace-pre-wrap text-sm text-foreground">
            {makerComment}
          </p>
        </div>
      )}

      {/* CHECKER COMMENT */}
      {checkerComment && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-600">
            Checker Comment
          </p>

          <p className="whitespace-pre-wrap text-sm text-red-800">
            {checkerComment}
          </p>
        </div>
      )}

      {/* IMAGES */}
      {(makerPhoto || checkerPhoto) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {makerPhoto && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />

                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Maker Image
                </p>
              </div>

              {makerPhoto ? (
                <img
                  src={resolveMediaUrl(makerPhoto)}
                  alt="Maker Upload"
                  className="h-52 w-full rounded-xl border border-border object-cover"
                />
              ) : (
                <div className="flex h-52 items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
                  No image available
                </div>
              )}
            </div>
          )}

          {checkerPhoto && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-red-500" />

                <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
                  Checker Objection Image
                </p>
              </div>

              {checkerPhoto ? (
                <img
                  src={resolveMediaUrl(checkerPhoto)}
                  alt="Checker Upload"
                  className="h-52 w-full rounded-xl border border-red-200 object-cover"
                />
              ) : (
                <div className="flex h-52 items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
                  No image available
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const normalizeRowIndex = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

function SafetyChecklistReadonlyModal({
  open,
  onClose,
  checklistId,
  pendingStatusLabel = "In Review",
  statusLabel,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [signatureUser, setSignatureUser] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(1);

  useEffect(() => {
    if (!open || !checklistId) {
      setDetail(null);
      setSignatureUser(null);
      return;
    }

    let mounted = true;

    const loadChecklist = async () => {
      try {
        setLoading(true);
        setDetail(null);
        setSignatureUser(null);

        const res = await getSafetyChecklist(checklistId);

        if (!mounted) return;

        setDetail(res?.data || null);
      } catch (err) {
        console.error("Failed to load checklist detail", err);

        if (mounted) {
          setDetail(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadChecklist();

    return () => {
      mounted = false;
    };
  }, [open, checklistId]);

  const itemInstances = useMemo(() => {
    if (!detail) return [];
    if (Array.isArray(detail.item_instances) && detail.item_instances.length > 0) {
      return [...detail.item_instances].sort((a, b) => Number(a.row_index) - Number(b.row_index));
    }
    const rowSet = new Set();
    (detail.items || []).forEach((item) => {
      (item.submissions || []).forEach((sub) => {
        rowSet.add(normalizeRowIndex(sub.row_index));
      });
    });
    const sortedRows = Array.from(rowSet).sort((a, b) => a - b);
    if (sortedRows.length === 0) sortedRows.push(1);
    return sortedRows.map((rIdx) => ({
      id: rIdx,
      row_index: rIdx,
      item_name: sortedRows.length > 1 ? `Item ${rIdx}` : (detail.name || `Item ${rIdx}`),
    }));
  }, [detail]);

  useEffect(() => {
    if (!open) return;
    const firstRowIndex = itemInstances[0]?.row_index || 1;
    const exists = itemInstances.some(
      (inst) => Number(inst.row_index) === Number(selectedRowIndex)
    );
    if (!exists) {
      setSelectedRowIndex(firstRowIndex);
    }
  }, [open, checklistId, itemInstances, selectedRowIndex]);

  const visibleQuestions = useMemo(() => {
    if (!detail?.items) return [];
    const activeRowIdx = normalizeRowIndex(selectedRowIndex);
    return detail.items.map((item) => {
      const subs = item.submissions || [];
      const matchingSub = subs.find(
        (sub) => normalizeRowIndex(sub.row_index) === activeRowIdx
      );
      return { item, sub: matchingSub || null };
    });
  }, [detail, selectedRowIndex]);

  const sectionedVisibleQuestions = useMemo(() => {
    return buildSectionedQuestionRows({
      questions: visibleQuestions,
      sectionConfig: detail?.template_section_config,
      getOrderIndex: (qEntry) => qEntry?.item?.template_order_index,
    });
  }, [visibleQuestions, detail?.template_section_config]);

  if (!open) return null;

  const isMultiItem = detail?.is_multi_item || itemInstances.length > 1;

  return (
    <div className="fixed inset-0 z-[201] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-border bg-background shadow-2xl">
        {/* HEADER */}
        <div className="flex items-start justify-between border-b border-border px-6 py-5">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <ChecklistStatusBadge status={statusLabel || pendingStatusLabel} />
            </div>

            <h2 className="text-xl font-bold text-foreground">
              {detail?.name || "Checklist Preview"}
            </h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Current checklist state preview
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-border bg-background p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-sm text-muted-foreground">
                Loading checklist...
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* INFO CARD */}
              <ReadonlyInfoCard detail={detail} />

              <InstructionBlock
                image={
                  detail?.template_instruction_image ||
                  detail?.instruction_image ||
                  detail?.instruction_image_url
                }
                text={
                  detail?.template_instruction_text || detail?.instruction_text
                }
                className="mb-5"
              />

              {/* MULTI ITEM SELECTOR DROPDOWN */}
              {isMultiItem && (
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-orange-50/70 p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-orange-900">
                      Equipment / Item:
                    </span>
                    <select
                      value={selectedRowIndex}
                      onChange={(e) => setSelectedRowIndex(Number(e.target.value))}
                      className="rounded-xl border border-orange-300 bg-white px-3.5 py-1.5 text-sm font-bold text-gray-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
                    >
                      {itemInstances.map((inst) => (
                        <option key={inst.row_index} value={inst.row_index}>
                          {inst.item_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-xs font-semibold text-orange-800">
                    Showing responses for{" "}
                    <span className="font-bold text-orange-950">
                      {itemInstances.find(
                        (i) => Number(i.row_index) === Number(selectedRowIndex)
                      )?.item_name || `Item ${selectedRowIndex}`}
                    </span>
                  </span>
                </div>
              )}

              {/* QUESTIONS */}
              <div className="space-y-4">
                {sectionedVisibleQuestions.map((entry, idx) => {
                  if (entry.type === "section") {
                    return (
                      <SafetyChecklistSectionHeader
                        key={`sec-${entry.key || idx}`}
                        label={entry.label}
                      />
                    );
                  }
                  const { item, sub } = entry.question;
                  return (
                    <ReadonlyQuestionCard
                      key={`${selectedRowIndex}-${item.id}`}
                      item={item}
                      sub={sub}
                      idx={entry.serial_no - 1}
                      displayIndex={entry.serial_no}
                      pendingStatusLabel={pendingStatusLabel}
                    />
                  );
                })}
              </div>

              {/* SIGN OFF */}
              <SignOffSection
                detail={detail}
                onViewSignature={setSignatureUser}
              />
            </div>
          )}
        </div>
      </div>

      <SignatureViewModal
        open={!!signatureUser}
        user={signatureUser}
        onClose={() => setSignatureUser(null)}
      />
    </div>
  );
}


export default SafetyChecklistReadonlyModal;
export {
  ReadonlyQuestionCard,
  SignOffSection,
  SignatureViewModal,
  getSignOffUsers,
};
